import { type ReactNode, useRef } from 'react';
import { twMerge } from 'tailwind-merge';
import Button from '@mui/material/Button';
import { useToggle } from '@/hooks/useToggle';
import { FilterIcon } from '@/icons';
import Badge from '@/components/Badge';
import { classes } from './utils';

export type SectionTitleProps = {
  title: string;
  newLabel?: string;
  filtersCount?: number;
  onAddNew?: () => void;
  renderControls?: () => ReactNode;
  renderFilterMenu?: (settings: {
    open: boolean;
    anchorTo: HTMLElement | null;
    onClose: () => void;
  }) => ReactNode;
};

export default function SectionTitle({
  title,
  newLabel = 'Add new',
  filtersCount = 0,
  renderFilterMenu,
  renderControls,
  onAddNew,
}: SectionTitleProps) {
  const menuToggleRef = useRef(null);
  const [isMenuOpen, toggleMenu] = useToggle();

  return (
    <div className={classes.wrapper}>
      <div className={classes.content}>
        <h1 className={classes.title}>{title}</h1>
        {renderFilterMenu && (
          <>
            <Badge
              className={
                filtersCount > 0 ? classes.activeBadge : classes.inactiveBadge
              }
              badgeContent={filtersCount}>
              <FilterIcon
                ref={menuToggleRef}
                className={twMerge(
                  classes.iconButton,
                  filtersCount > 0 && classes.activeFilters
                )}
                onClick={() => toggleMenu()}
              />
            </Badge>
            {renderFilterMenu({
              open: isMenuOpen,
              anchorTo: menuToggleRef.current,
              onClose: () => toggleMenu(),
            })}
          </>
        )}
      </div>
      <div className={classes.controls}>
        {renderControls && renderControls()}
        {onAddNew && (
          <Button
            className={classes.addButton}
            color="orange-3"
            onClick={onAddNew}>
            {newLabel}
          </Button>
        )}
      </div>
    </div>
  );
}
